/**
 * Utility functions for handling video in the PDPVideo component
 */

import { applyVideoFetchPrefix } from '../../utils/videoUrlPrefix';

/**
 * Safely parse JSON with error handling
 * @param {HTMLElement} element - DOM element containing JSON
 * @param {string} errorPrefix - Prefix for error messages
 * @returns {Object|null} - Parsed JSON object or null if error
 */
export function parseJSON(element, errorPrefix) {
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (e) {
    console.error(`🎬 ${errorPrefix}:`, e);
    return null;
  }
}

/**
 * Create a debounced function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait between calls
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Safely play a video with error handling
 * @param {HTMLVideoElement} video - Video element to play
 * @returns {Promise} - Promise that resolves when video plays or rejects if error
 */
export function playVideo(video) {
  if (!video) return Promise.reject(new Error('No video element provided'));
  return video.play().catch((err) => console.warn('🎬 Could not play video:', err));
}

/**
 * Get the metafield filename for a given barcode
 * @returns {string|null} - Filename from metafield or null
 */
export function getMetafieldFilename() {
  // Implementation would depend on how metafields are accessed
  // This is a placeholder function
  return null;
}

/**
 * Get video URL based on video object properties
 * @param {Object} video - Video object with source information
 * @param {boolean} preferThumbnail - Whether to return thumbnail URL instead of video
 * @returns {string|null} - URL for the video or null if not available
 */
export function getVideoUrl(video, preferThumbnail = false) {
  if (!video) return null;

  // For thumbnails, try to get preview image
  if (preferThumbnail) {
    // First check for preview_image property from JSON data
    if (video.preview_image) {
      return video.preview_image.startsWith('/') ? video.preview_image : `/${video.preview_image}`;
    }

    // Then check sources array (Shopify admin format)
    if (video.sources && Array.isArray(video.sources)) {
      const previewImage = video.sources.find(
        (source) => source.format === 'preview_image' || source.format === 'thumbnail_image',
      );

      if (previewImage && previewImage.url) {
        return previewImage.url;
      }
    }

    // Fallback to thumbnail from video ID
    if (video.id) {
      return `https://cdn.shopify.com/videos/c/vp/${video.id}/thumbnail.jpg`;
    }
  }

  // For video slides, use HD version when available
  if (
    !preferThumbnail &&
    video.sources &&
    Array.isArray(video.sources) &&
    video.sources.length > 1 &&
    video.sources[1] &&
    video.sources[1].url
  ) {
    return video.sources[1].url;
  }

  // Priority-based URL resolution for video sources
  if (video.url && (video.url.includes('cdn.shopify.com/videos') || video.url.includes('cdn.shopify.com/s/files'))) {
    return video.url;
  }

  if (video.file_url) {
    return video.file_url.startsWith('/') ? video.file_url : `/${video.file_url}`;
  }

  if (video.url) {
    return video.url.startsWith('/') ? video.url : `/${video.url}`;
  }

  if (video.filename) {
    let filename = video.filename.toLowerCase();
    const supportedFormats = ['.mp4', '.mov', '.webm'];
    const hasVideoExtension = supportedFormats.some((ext) => filename.endsWith(ext));
    if (!hasVideoExtension) {
      filename = `${video.filename}.mp4`; // fallback to mp4
    } else {
      filename = video.filename;
    }
    return `/cdn/shop/files/${filename}`;
  }

  return null;
}

/**
 * Builds an IntersectionObserver callback that lazy-loads the video source on first
 * intersection, plays while visible, and pauses when scrolled out of viewport.
 * Pausing on exit stops Cloudinary range requests for off-screen autoplay loops.
 * @param {String} [videoUrl] - optional video url (closure-passed). When omitted,
 *   the callback reads `data-src` from the <source> element, allowing the same
 *   callback to drive both dynamically-created and Liquid-rendered slides.
 * @returns {Function}
 */
export const generateVideoObserverCallback = (videoUrl) => {
  return (entries) => {
    entries.forEach((entry) => {
      const videoElement = entry.target.querySelector('video');
      const videoSource = entry.target.querySelector('source');

      if (!videoElement || !videoSource) {
        return console.error('There was no video element found');
      }

      if (entry.isIntersecting) {
        // NOTE: use getAttribute('src'), not .src. The IDL `.src` resolves an
        // empty `src=""` attribute against the document URL and returns the
        // document URL — so `!videoSource.src` would always be false for the
        // CarouselManager template `<source src="" type="video/mp4">`.
        if (!videoSource.getAttribute('src')) {
          const target = videoSource.dataset.src || videoUrl;
          if (target) {
            videoSource.src = applyVideoFetchPrefix(target, 'pdp');
            videoElement.load();
          }
        }
        videoElement.play().catch(() => {});
      } else {
        videoElement.pause();
      }
    });
  };
};

/**
 * Build a observer option object
 * @param {HTMLElement} rootElement
 * @returns {Object} minimum and custom observer properties
 */
export const generateVideoObserverOptions = (rootElement) => {
  return {
    root: rootElement || null,
    rootMargin: '0px',
    threshold: 0.01, // Don't use 1.0 because it is unrealistic to meet on Safari browsers
  };
};
